import { Trans, useLingui } from '@lingui/react/macro';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import LaunchIcon from '@mui/icons-material/Launch';
import LogoutIcon from '@mui/icons-material/Logout';
import { Box, Button, Chip, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useCopyToClipboard } from 'usehooks-ts';
import { erc20Abi, formatUnits, getAddress } from 'viem';
import { useAccount, useBalance, useDisconnect, useReadContract } from 'wagmi';
import { getBlockExplorerAddressUrl } from '@/common/blockExplorer';
import { formatTokenAmountDisplay } from '@/utils/tokenNumberUtils';
import useAppStore, { useAccountBalance, useLockedBalance } from '../../../store/rootStore';

const COLLATERAL_FORMAT_OPTS = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
} as const;

interface AccountPanelProps {
  onDisconnect?: () => void;
  showDisconnect?: boolean;
}

export function AccountPanel({ onDisconnect, showDisconnect = true }: AccountPanelProps) {
  const { t } = useLingui();
  const { address } = useAccount();
  const { disconnect } = useDisconnect();
  const [, copy] = useCopyToClipboard();

  const isDelegated = useAppStore((state) => state.isDelegated);
  const isDelegatedOwner = useAppStore((state) => state.isDelegatedOwner);
  const activeDelegatedAddress = useAppStore((state) => state.activeDelegatedAddress);
  const token = useAppStore((state) => state.exchange?.token);
  const chainId = useAppStore((state) => state.config?.chain.id);

  const tokenAddress = token?.address ? getAddress(token.address) : undefined;
  const delegatedAddress = isDelegated && activeDelegatedAddress ? getAddress(activeDelegatedAddress) : undefined;
  const walletBalanceAddress = isDelegated ? delegatedAddress : address;

  const { data: nativeBalance } = useBalance({
    address: walletBalanceAddress,
    chainId,
  });

  const { data: collateralBalance } = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: walletBalanceAddress ? [walletBalanceAddress] : undefined,
    chainId,
    query: { enabled: !!tokenAddress && !!walletBalanceAddress },
  });

  const availableBalance = useAccountBalance();
  const lockedBalance = useLockedBalance();

  const handleCopy = (text: string) => {
    copy(text);
  };

  const handleDisconnect = () => {
    disconnect();
    onDisconnect?.();
  };

  return (
    <Stack spacing={2}>
      {isDelegated && activeDelegatedAddress ? (
        <Box>
          <Typography variant="subtitle2" color="warning.main">
            <Trans>Delegated Account</Trans>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
              {`${activeDelegatedAddress.substring(0, 6)}...${activeDelegatedAddress.substring(activeDelegatedAddress.length - 4)}`}
            </Typography>
            <Tooltip title={t`Copy Delegated Address`}>
              <IconButton size="small" onClick={() => handleCopy(activeDelegatedAddress)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t`View on Explorer`}>
              <IconButton
                size="small"
                component="a"
                href={getBlockExplorerAddressUrl(activeDelegatedAddress)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LaunchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      ) : null}

      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            <Trans>Connected Wallet</Trans>
          </Typography>
          {isDelegatedOwner ? (
            <Chip
              label={t({ comment: 'Delegated account owner chip', message: 'Owner' })}
              size="small"
              color="primary"
              sx={{ height: 18, fontSize: '0.625rem' }}
            />
          ) : null}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
            {address ? `${address.substring(0, 6)}...${address.substring(address.length - 4)}` : ''}
          </Typography>
          {address ? (
            <>
              <Tooltip title={t`Copy Address`}>
                <IconButton size="small" onClick={() => handleCopy(address)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t`View on Explorer`}>
                <IconButton
                  size="small"
                  component="a"
                  href={getBlockExplorerAddressUrl(address)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : null}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          <Trans>Exchange</Trans>
        </Typography>
        {token ? (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">
                <Trans>Available ({token.symbol})</Trans>
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatTokenAmountDisplay(availableBalance, token, COLLATERAL_FORMAT_OPTS)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <Trans>Locked ({token.symbol})</Trans>
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {lockedBalance ? formatTokenAmountDisplay(lockedBalance, token, COLLATERAL_FORMAT_OPTS) : '0.00'}
              </Typography>
            </Stack>
          </>
        ) : null}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" color={isDelegated ? 'warning.main' : 'text.secondary'} gutterBottom>
          {isDelegated ? t`Delegated Account` : t`Wallet`}
        </Typography>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2">
            <Trans>Native ({nativeBalance?.symbol})</Trans>
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {nativeBalance ? `${parseFloat(formatUnits(nativeBalance.value, nativeBalance.decimals)).toFixed(4)}` : '0.0000'}
          </Typography>
        </Stack>

        {token ? (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">
              <Trans>Collateral ({token.symbol})</Trans>
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {collateralBalance !== undefined
                ? formatTokenAmountDisplay(collateralBalance, token, COLLATERAL_FORMAT_OPTS)
                : '0.00'}
            </Typography>
          </Stack>
        ) : null}
      </Box>

      {showDisconnect ? (
        <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={handleDisconnect} fullWidth>
          <Trans>Disconnect</Trans>
        </Button>
      ) : null}
    </Stack>
  );
}
